import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TIERS, isSuperAdmin, tierLabel, type Tier } from '@foundry/shared';
import { useMe } from '../../api/auth.js';
import {
  useDeleteCall,
  useEndUsers,
  useRecordCall,
  useSetEndUserPassword,
  useUpdateEndUser,
  useUserCalls,
} from '../../api/users.js';
import type { EndUser } from '../../api/types.js';
import {
  Alert,
  Button,
  Card,
  Field,
  PasswordField,
  PasswordRules,
  Spinner,
  isPasswordValid,
} from '../../components/ui.js';

/** The user's full name for the table, or an em dash if not captured. */
function fullName(u: EndUser): string {
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return name || '—';
}

/** Super-admin management of end-user (founder) accounts, as a compact table. */
export function UsersPage() {
  const { data: user } = useMe();
  const usersQ = useEndUsers();

  if (user && !isSuperAdmin(user.role)) return <Navigate to="/console" replace />;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">End users</h1>
        <p className="opacity-70">
          Manage founder accounts: their package, access, details, and password.
        </p>
      </div>

      {usersQ.isPending ? (
        <Spinner />
      ) : usersQ.isError ? (
        <Alert>Could not load end users.</Alert>
      ) : usersQ.data.length === 0 ? (
        <p className="opacity-70">No end users yet — invite one from the People page.</p>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-secondary/10 text-left opacity-60">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Package</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {usersQ.data.map((u) => (
                  <UserTableRow key={u.id} user={u} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function UserTableRow({ user }: { user: EndUser }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-secondary/5 align-middle">
        <td className="py-2 pr-4">{fullName(user)}</td>
        <td className="py-2 pr-4">{user.email}</td>
        <td className="py-2 pr-4">{tierLabel(user.tier)}</td>
        <td className="py-2 pr-4">
          {user.blocked ? (
            <span className="text-red-700">Blocked</span>
          ) : (
            <span className="text-primary">Active</span>
          )}
        </td>
        <td className="py-2 text-right">
          <button className="text-sm text-primary underline" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : 'Manage'}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-secondary/10 bg-neutral/40">
          <td colSpan={5} className="px-3 py-4">
            <UserDetail user={user} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function UserDetail({ user }: { user: EndUser }) {
  const update = useUpdateEndUser();
  const setPassword = useSetEndUserPassword();
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [email, setEmail] = useState(user.email);
  const [tier, setTier] = useState<Tier>(user.tier);
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState('');
  const [pwDone, setPwDone] = useState(false);
  const [showCalls, setShowCalls] = useState(false);

  const dirty =
    email.trim() !== user.email ||
    tier !== user.tier ||
    firstName.trim() !== (user.firstName ?? '') ||
    lastName.trim() !== (user.lastName ?? '');

  const onSave = (): void =>
    update.mutate({
      id: user.id,
      email: email.trim(),
      tier,
      ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Field label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        <div className="min-w-56 flex-1">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Package</span>
          <select
            className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            value={tier}
            onChange={(e) => setTier(e.target.value as Tier)}
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {tierLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <Button disabled={!dirty || update.isPending} onClick={onSave}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          variant={user.blocked ? 'secondary' : 'danger'}
          disabled={update.isPending}
          onClick={() => update.mutate({ id: user.id, blocked: !user.blocked })}
        >
          {user.blocked ? 'Unblock' : 'Block'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setResetting((v) => !v);
            setPwDone(false);
          }}
        >
          {resetting ? 'Cancel reset' : 'Reset password'}
        </Button>
        <Button variant="secondary" onClick={() => setShowCalls((v) => !v)}>
          {showCalls ? 'Hide calls' : 'Calls'}
        </Button>
      </div>

      {update.isError ? <Alert>Could not save — the email may already be in use.</Alert> : null}
      {pwDone ? (
        <p className="text-sm text-primary">
          Password reset. The user must choose a new one at next sign-in.
        </p>
      ) : null}

      {resetting ? (
        <div className="flex flex-col gap-2 border-t border-secondary/10 pt-3">
          <PasswordField
            label="New temporary password"
            autoComplete="new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
          <PasswordRules value={pw} />
          <div>
            <Button
              disabled={setPassword.isPending || !isPasswordValid(pw)}
              onClick={() =>
                setPassword.mutate(
                  { id: user.id, password: pw },
                  {
                    onSuccess: () => {
                      setPwDone(true);
                      setResetting(false);
                      setPw('');
                    },
                  },
                )
              }
            >
              {setPassword.isPending ? 'Setting…' : 'Set password'}
            </Button>
          </div>
        </div>
      ) : null}

      {showCalls ? (
        <div className="border-t border-secondary/10 pt-3">
          <UserCalls userId={user.id} />
        </div>
      ) : null}
    </div>
  );
}

/** Today as an `yyyy-mm-dd` string (local), for the call date default. */
function todayISODate(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function UserCalls({ userId }: { userId: string }) {
  const q = useUserCalls(userId);
  const record = useRecordCall(userId);
  const del = useDeleteCall(userId);
  const [optionKey, setOptionKey] = useState('');
  const [date, setDate] = useState(todayISODate());
  const [notes, setNotes] = useState('');

  if (q.isPending) return <Spinner />;
  if (q.isError || !q.data) return <Alert>Could not load calls.</Alert>;

  const { status, calls } = q.data;
  const selected = status.options.find((o) => o.key === optionKey);
  const atLimit = selected ? selected.remaining <= 0 : false;

  const onRecord = (): void => {
    if (!optionKey || atLimit) return;
    record.mutate(
      { optionKey, occurredAt: new Date(`${date}T12:00:00Z`).toISOString(), notes: notes.trim() },
      { onSuccess: () => setNotes('') },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {status.options.length === 0 ? (
        <p className="text-sm opacity-60">This tier has no call types enabled.</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {status.options.map((o) => (
            <li key={o.key} className="flex justify-between gap-2">
              <span>{o.label}</span>
              <span className={o.remaining <= 0 ? 'text-red-700' : 'opacity-70'}>
                {o.usedThisMonth} of {o.maxPerMonth} this month
                {o.remaining <= 0 ? ' · limit reached' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      {status.options.length > 0 ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Call type</span>
            <select
              className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary"
              value={optionKey}
              onChange={(e) => setOptionKey(e.target.value)}
            >
              <option value="">Choose…</option>
              {status.options.map((o) => (
                <option key={o.key} value={o.key} disabled={o.remaining <= 0}>
                  {o.label}
                  {o.remaining <= 0 ? ' (limit reached)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Date</span>
            <input
              type="date"
              className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <div className="min-w-40 flex-1">
            <Field
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <Button
            disabled={record.isPending || atLimit || !optionKey}
            title={atLimit ? 'This call type’s monthly limit is reached' : undefined}
            onClick={onRecord}
          >
            {record.isPending ? 'Recording…' : 'Record call'}
          </Button>
        </div>
      ) : null}
      {record.isError ? <Alert>Could not record the call.</Alert> : null}

      {calls.length > 0 ? (
        <ul className="flex flex-col gap-1 text-sm">
          {calls.map((c) => (
            <li key={c.id} className="flex items-center gap-2 rounded bg-white p-2">
              <span className="opacity-60">{new Date(c.occurredAt).toLocaleDateString()}</span>
              <span className="flex-1">{c.label}</span>
              {c.notes ? <span className="opacity-60">{c.notes}</span> : null}
              <button className="text-xs text-red-700 underline" onClick={() => del.mutate(c.id)}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
